import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../contexts/AppContext';
import InputWithCounter from '../components/utils/InputWithCounter';
import FloatingFooter from '../components/utils/FloatingFooter';
import ConfirmModal from '../components/utils/ConfirmModal';
import InformModal from '../components/utils/InformModal';
import ImageUploader from '../components/utils/ImageUploader';
import { FaTrash, FaArrowUp, FaArrowDown, FaDownload } from 'react-icons/fa';
import { message } from 'antd';

const MAX_IMG = 6;
const IMG_W = 960;
const IMG_H = 320;

const buildLandingSnapshot = (landing = {}) => ({
  title: landing.serviceTitle || '',
  intro: landing.serviceDescription || '',
  imgs: Array.isArray(landing.serviceImages) ? landing.serviceImages : [],
});

const ServiceLayout = () => {
  const { userInfo, selectedRole, token } = useContext(AppContext);
  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;

  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [imgs, setImgs] = useState([]);
  const [origLanding, setOrigLanding] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [inform, setInform] = useState({ open: false, msg: '', type: 'inform' });

  useEffect(() => {
    const arr = userInfo?.roleInfo?.[selectedRole.type] ?? [];
    const cur = arr.find((r) => r.id === selectedRole.id);
    if (cur?.landing) {
      const ln = cur.landing;
      const snapshot = buildLandingSnapshot(ln);
      setOrigLanding(ln);
      setTitle(snapshot.title);
      setIntro(snapshot.intro);
      setImgs(snapshot.imgs);
      setInitialSnapshot(snapshot);
    }
  }, [userInfo, selectedRole]);

  const hasChanges = useMemo(() => {
    if (!initialSnapshot) return false;

    return JSON.stringify({
      title,
      intro,
      imgs,
    }) !== JSON.stringify(initialSnapshot);
  }, [title, intro, imgs, initialSnapshot]);

  const swap = (i, j) =>
    setImgs((arr) => {
      const a = [...arr];
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });

  const up = (i) => i > 0 && swap(i, i - 1);
  const down = (i) => i < imgs.length - 1 && swap(i, i + 1);
  const del = (i) => setImgs((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/property/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            landing: {
              ...origLanding,
              serviceTitle: title,
              serviceDescription: intro,
              serviceImages: imgs,
            },
          }),
        }
      );
      setConfirm(false);
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || resp.statusText);
      }
      window.location.reload();
    } catch (e) {
      setInform({ open: true, msg: e.message, type: 'error' });
    }
  };

  return (
    <div className="mx-auto mb-20 w-[90%] max-w-7xl">
      <div className="mt-4 overflow-hidden rounded-[30px] border border-[#e2e7f6] bg-white shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
        <div className="divide-y divide-[#e8edf8] px-7 py-4">
          <section className="py-4 transition-colors duration-200 hover:bg-[#fcfdff]">
            <div className="mb-0.5">
               <div className="cms-section-title text-[#1f2a44]">Title</div>
            </div>
            <InputWithCounter
              compact
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
            />
          </section>

          <section className="py-4 transition-colors duration-200 hover:bg-[#fcfdff]">
            <div className="mb-0.5">
               <div className="cms-section-title text-[#1f2a44]">
                 Introduction
               </div>
            </div>
            <InputWithCounter
              compact
              multiline
              rows={4}
              value={intro}
              maxLength={2000}
              onChange={(e) => setIntro(e.target.value)}
            />
          </section>

          <section className="py-4 transition-colors duration-200 hover:bg-[#fcfdff]">
            <div className="mb-2">
               <div className="cms-section-title text-[#1f2a44]">
                 Carousel Images
               </div>
            </div>

            <div className="space-y-3">
              {imgs.map((u, i) => (
                <div
                  key={`${u}-${i}`}
                  className="group grid grid-cols-1 gap-7 border-b border-[#e8edf8] px-2 py-5 transition-colors duration-200 hover:bg-[#fbfcff] xl:grid-cols-[180px_340px_minmax(420px,1fr)]"
                >
                  <div className="flex items-center gap-6 xl:pr-4">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#4a5cff] text-sm font-semibold text-white shadow-[0_4px_10px_rgba(39,58,168,0.14)] ring-1 ring-[rgba(255,255,255,0.10)] transition-transform duration-200 group-hover:scale-[1.02]">
                      {i + 1}
                    </span>
                    <div>
                      <div className="cms-card-title text-[#23304a]">
                        Slide {i + 1}
                      </div>
                      <div className="mt-2 flex gap-2 text-[#7f8aa4]">
                        <button
                          disabled={i === 0}
                          onClick={() => up(i)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e1e6f3] bg-white transition-all duration-200 hover:-translate-y-[1px] hover:border-[#cfd8ef] hover:bg-[#f8faff] hover:text-[#27344f] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FaArrowUp />
                        </button>
                        <button
                          disabled={i === imgs.length - 1}
                          onClick={() => down(i)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e1e6f3] bg-white transition-all duration-200 hover:-translate-y-[1px] hover:border-[#cfd8ef] hover:bg-[#f8faff] hover:text-[#27344f] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FaArrowDown />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-[20px] border border-[#e6ebf8] bg-[#f6f8ff] p-2.5 transition-all duration-200 hover:border-[#d4ddf8] hover:shadow-[0_10px_22px_rgba(74,92,255,0.08)]">
                    <img
                      src={u}
                      alt={`Service ${i + 1}`}
                      className="h-[150px] w-full rounded-[16px] object-cover"
                    />
                  </div>

                  <div className="flex flex-col justify-center xl:pl-28">
                    <div className="flex max-w-[380px] flex-col gap-5">
                      <div className="cms-eyebrow text-[#8a97b4]">
                        Media Actions
                      </div>
                      <div className="flex items-center gap-5">
                        <button
                          disabled={imgs.length === 1}
                          onClick={() => del(i)}
                          className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#ffd9e0] bg-[#fff5f7] text-[#ff4d6d] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ffc5cf] hover:bg-[#fff0f3] hover:shadow-[0_10px_20px_rgba(255,77,109,0.12)] ${
                            imgs.length === 1 ? 'cursor-not-allowed opacity-40' : ''
                          }`}
                        >
                          <FaTrash />
                        </button>
                        <a
                          href={u}
                          download
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#d9e3ff] bg-[#f4f7ff] text-[#4a5cff] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c8d3fb] hover:bg-[#eef2ff] hover:shadow-[0_10px_20px_rgba(74,92,255,0.12)]"
                        >
                          <FaDownload />
                        </a>
                      </div>
                      <div className="flex items-center justify-start pt-1">
                        <ImageUploader
                          token={token}
                          width={IMG_W}
                          height={IMG_H}
                          prefix={prefix}
                          thumbPrefix={thumbPrefix}
                          buttonText="Replace Image"
                          onChange={(url) => {
                            setImgs((arr) => arr.map((img, idx) => (idx === i ? url : img)));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {imgs.length < MAX_IMG ? (
              <div className="mt-4 rounded-[20px] border border-dashed border-[#d7def3] bg-[#fafbff] p-4 transition-all duration-200 hover:border-[#bdcaf3] hover:bg-[#f7f9ff] hover:shadow-[0_12px_24px_rgba(74,92,255,0.08)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div />
                  <div className="relative">
                    <ImageUploader
                      token={token}
                      width={IMG_W}
                      height={IMG_H}
                      prefix={prefix}
                      thumbPrefix={thumbPrefix}
                      onChange={(url) => {
                        setImgs((arr) => [...arr, url]);
                      }}
                      buttonText="Add New Slide Image"
                    />
                    {userInfo?.permissionLevel === 0 && imgs.length >= 2 && (
                      <button
                        type="button"
                        className="absolute inset-0 h-full w-full bg-transparent"
                        onClick={() =>
                          message.error(
                            'Your package level does not support adding more than 2 images. Please contact support to upgrade your package.'
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="mt-5 rounded-2xl border border-[#ffd6de] bg-[#fff6f8] px-4 py-3 text-[13px] text-[#d64563]">
                You have reached the maximum of {MAX_IMG} service images.
              </p>
            )}
          </section>
        </div>
      </div>

      <FloatingFooter hasChanges={hasChanges} onSave={() => setConfirm(true)} />
      <ConfirmModal
        isOpen={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={save}
        header="Confirm Save"
        content="Save service images?"
      />
      <InformModal
        isOpen={inform.open}
        onClose={() => setInform({ ...inform, open: false })}
        content={inform.msg}
        type={inform.type}
      />
    </div>
  );
};

export default ServiceLayout;
